import { EventEmitter } from 'events';
import deepEqual from 'fast-deep-equal';

import {
  History,
  HistoryArrayFlags,
  HistoryEntry,
  HistoryEvents,
  HistoryJSON,
  HistoryOptions,
} from './types.js';
import {
  historyCreateEntry,
  historyCreateKeyPair,
  historyFindByStep,
  historyFromJSON,
  historySignature,
  historyToArray,
  historyToGenerator,
  historyToJSON,
  historyVerify,
} from './utils.js';

export class HistoryImpl<TState, TMeta>
  extends EventEmitter<HistoryEvents>
  implements History<TState, TMeta>
{
  head: HistoryEntry<TState, TMeta>;
  signature: string;
  dedupe: boolean = false;
  equalityFn: (a: TState, b: TState) => boolean = deepEqual;

  get hasNextStep() {
    return this.head.next !== null;
  }

  get hasPreviousStep() {
    return this.head.prev !== null;
  }

  get index() {
    return this.head.index;
  }

  size: number = 1;

  /**
   *
   * @param head
   * @param opt.dedupe - Remove consecutive duplicate states from history default true
   *
   */
  constructor(head: HistoryEntry<TState, TMeta>, opts?: HistoryOptions<TState>) {
    super();
    const { wif, dedupe, equalityFn } = { ...opts };

    const keyPair = historyCreateKeyPair(wif);

    this.head = head;
    this.signature = historySignature(keyPair, this.head);
    this.dedupe = dedupe ?? this.dedupe;
    this.equalityFn = equalityFn ?? this.equalityFn;
    this.on('change', () => {
      this.signature = historySignature(keyPair, this.head);
    });
    this.verify = (signature: string) => historyVerify(keyPair, this.head, signature);
  }

  go(steps: number | string) {
    const head = historyFindByStep(this, steps);
    this.head = head;
    this.emit('change');
    return this;
  }

  undo() {
    return this.go(-1);
  }

  redo() {
    return this.go(1);
  }

  // eslint-disable-next-line no-unused-vars, unused-imports/no-unused-vars
  verify(signature: string): boolean {
    throw new Error('History verify function not implemented');
  }

  push(state: TState, meta?: TMeta) {
    if (this.dedupe && this.equalityFn(this.head.state, state)) {
      return this;
    }

    const entry = historyCreateEntry(state, meta, this.head);
    this.head = entry;
    this.size = entry.index + 1;
    this.emit('change');
    return this;
  }

  reset(initialState: TState, meta?: TMeta): this {
    this.head = historyCreateEntry(initialState, meta);
    this.size = 1;
    this.emit('change');
    return this;
  }

  subscribe(cb: () => void) {
    const listener = () => cb();
    this.on('change', listener);
    return () => {
      this.off('change', listener);
    };
  }

  toGenerator() {
    return historyToGenerator(this.head);
  }

  toArray(flags?: HistoryArrayFlags) {
    return historyToArray(this.head, flags);
  }

  toJSON(): HistoryJSON<TState, TMeta> {
    return historyToJSON(this);
  }

  fromJSON(json: any): this {
    historyFromJSON<TState, TMeta>(this, json);
    this.emit('change');
    return this;
  }

  *[Symbol.iterator]() {
    yield* historyToGenerator(this.head);
  }
}

export const createHistory = <TState, TMeta>(
  initialState: TState,
  meta?: TMeta,
  opts?: HistoryOptions<TState>
): History<TState, TMeta> => {
  const head = historyCreateEntry(initialState, meta);
  return new HistoryImpl<TState, TMeta>(head, opts);
};
